/**
 * Template tools — search_templates + place_template.
 *
 * Placement is deterministic (no generation call): the template's container
 * tree is materialized as an invisible Layout skeleton with named slot
 * objects, and the result lists each slot's path and author hint. The agent
 * then fills slots with the ordinary creation tools (create_object,
 * build_spatial with parent, generate_image, ...) — a separate verb from
 * generation on purpose: placing structure has no failure modes worth
 * spending model tokens on.
 */

import type { ToolDefinition } from './types';
import { editModeOnly } from './authoring-ops';
import type { ToolContext } from '../agent-helpers';
import { applyTransform, recordCreateForUndo, resolveObject } from '../agent-helpers';
import type { SceneObject } from 'trace-viewer/scene';
import type { CreateChildEntry, UndoCommand, WrapperOperation } from 'trace-viewer/editor';
import { CompositeCommand, RenameCommand, TransformCommand, WrapperOperationError, changeWrapperSet, copyForInsideEdit as copyReferencesForInsideEdit, findWrapper, findWrapperSlot, getUndoManager, isStructuralRoot, placeWrapper, resizeWrapper, swapWrapper, topLevelParent, wrapContent } from 'trace-viewer/editor';
import { Layout, Provenance } from 'trace-viewer/behaviors';
import type { RendererData } from 'trace-viewer/behaviors';
import { resolveMaterialRole, rolePropertyName } from 'trace-viewer/editor';
import { convertTemplate, DEFAULT_PALETTE, DU_TO_M, type TemplatePalette } from '../templates/convert';
import { readVec3, setThemeValues } from 'trace-viewer/editor';
import { getTemplate, searchTemplates } from '../templates/library';
import { WRAPPER_LIBRARY, COMPONENT_MATERIALS, COMPONENT_METRICS, getWrapper, searchWrappers, defaultSize } from '../templates/wrappers';
import { getStyle, mapFontToBundled, type LibraryStyle } from '../styles/library';
import type { TemplateArtifact, TemplateSlot } from '../templates/types';
import { initHostFontPacks } from '../../host-font-packs';
import { getWrapperLibrary } from '../wrapper-library';
import { getActiveCmsManager } from '../../cms/active-manager';

// ── search_templates ───────────────────────────────────────

function describeTemplate(t: TemplateArtifact): string {
    const size = t.defaultSize
        ? ` (~${(t.defaultSize.x / 1000).toFixed(2)}×${(t.defaultSize.y / 1000).toFixed(2)}m)`
        : '';
    const { slots } = convertTemplate(t);
    const when = t.categoryTags?.when ?? t.description?.split('\n')[0] ?? '';
    return `- ${t.slug}${size} — ${when} [${slots.length} slot${slots.length === 1 ? '' : 's'}]`;
}

async function executeSearchTemplates(
    input: Record<string, unknown>,
): Promise<string> {
    const query = input.query as string | undefined;
    const results = searchTemplates(query);
    if (results.length === 0) {
        return `No templates match "${query ?? ''}". Call search_templates without a query to list all.`;
    }
    return `${results.length} template(s):\n${results.map(describeTemplate).join('\n')}\n\nUse place_template with a slug to place one.`;
}

// ── place_template ─────────────────────────────────────────

/**
 * The fill-and-stage procedure place_template returns with its result.
 *
 * This is prompt text in everything but location: it is the model's main
 * instruction on how to compose into a template, and the reason it stages the
 * result at all. It rides the result rather than the templates kit's section
 * because it is only useful once a template has actually been placed.
 * `{accent}` and `{text}` are substituted per call.
 */
export const PLACE_TEMPLATE_GUIDANCE = `The template is a guide: adapt or extend its layout as needed, keeping text, captions and panels in engine auto-layout. These slot rules apply to content kept in this layout.
How to fill slots — ONE build_spatial call can fill them all. Use the auto-layout helpers and let the template own all spacing:
1. Per slot, create ONE object and parent it into the slot: parent(handle, "<slot path>"). That object is a stack()/card() for flowed content, or a plain group() for a free 3D build — never a group() around flowed content, and never a card() around a 3D build (its Body slab sits in front of it). Compose content INSIDE it with label(), image(), card(), stack(); never set positions on flowed children, and never move() a slot fill in z — the template owns depth.
2. NEVER position content relative to another slot, and never offset content to reach outside its slot — each slot's content must fit that slot. If the design needs a section the template lacks (e.g. a gallery band), extend its auto-layout structure or place a second template (e.g. "mosaic") next to or below this one instead of free-floating content. Never parent content into "* Body" objects — those are the slots' background slabs, not fill points.
3. image(prompt) with NO size, as a slot's direct child, fills the slot with the template's fit — that is how a hero dominates; size it (image(prompt, w, h)) only inside a sized stack. It renders after the build automatically (do NOT call generate_image for slot art). Use real images generously — heroes, poster art, gallery stills.
4. label() sizes text via fontSize (hinted fontSize N → N/10); slot hint sizes are mm (÷1000 for meters). Use the accent color ({accent}) for CTAs, badges, and highlights; body text {text}. Text wraps to the width of a sized container (a stack with a width, or one filling its slot); in a Hug stack it runs on, so size the container rather than breaking copy with \\n (a line is ~ chars × fontSize×0.0055 m wide).
5. Free 3D compositions kept in this layout (product mockups, sculptural builds) use primitives with explicit positions inside ONE plain group() parented into a single [model] slot — never inside a card().
6. STAGE the finished piece — a floating panel in a void reads as unfinished. AFTER filling all slots, call stage_display with this root as the target (use the picked wrapper when supplied; otherwise pick a wrapper whose kind fits: backdrop/exhibition for panels, frame for posters, base for product pieces) — it builds a designed set around the canvas deterministically. Judges and humans consistently prefer a staged set piece over a bare panel.
7. Multi-panel scenes: group ALL panels under ONE parent group first (side by side via a horizontal stack or explicit positions), then stage that group once — never stage one panel and leave siblings floating near the set (they collide with the stage geometry).`;

const ANCHOR_NOTE: Record<NonNullable<TemplateSlot['anchor']>, string> = {
    behind: 'sunk into the surface behind it',
    centered: 'centred on the surface behind it, like an exhibit on a tabletop',
    forward: 'popped toward the viewer, clear of the surface behind it',
};

/**
 * The result line naming the arguments a chat panel pin replaced, or '' when
 * nothing was pinned. Entries are [argument, pinned value, value the call passed].
 */
function pinNote(entries: [string, string | undefined, string | undefined][]): string {
    const parts = entries.flatMap(([arg, pinned, requested]) => pinned
        ? [`${arg} "${pinned}"${requested && requested !== pinned ? ` (the call asked for "${requested}")` : ''}`]
        : []);
    return parts.length ? `\nPinned by the user and used regardless of the call's arguments: ${parts.join(', ')}.` : '';
}

function formatSlots(rootName: string, slots: TemplateSlot[], actualPaths: string[]): string {
    return slots.map(s => {
        const path = actualPaths[s.nodeIndex];
        const full = path ? `${rootName}/${path}` : rootName;
        const opt = s.optional ? ' (optional — delete the object if not needed)' : '';
        // The skeleton already seats a model slot in depth (its alignCrossB
        // carries the template's modelAnchor), so this is orientation, not an
        // instruction to position anything by hand.
        const anchor = s.anchor
            ? `\n  Depth: this slot seats its model ${ANCHOR_NOTE[s.anchor]} — the slot handles it; do not offset the model yourself.`
            : '';
        return `- [${s.kind}] ${full}${opt}\n  ${s.hint ?? 'Fill with content that fits the template.'}${anchor}`;
    }).join('\n');
}

async function executePlaceTemplate(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    // A picked template is a guide: default to it, but let the agent adapt or
    // combine layouts. A picked style remains required and wins over the call.
    const pins = ctx.assistantPins ?? {};
    const requestedSlug = (input.slug as string | undefined)?.trim();
    const slug = requestedSlug || pins.template;
    if (!slug) return 'Error: "slug" is required — a template slug from search_templates.';
    const artifact = getTemplate(slug);
    if (!artifact) {
        return `Error: No template "${slug}". Available: ${searchTemplates().map(t => t.slug).join(', ')}`;
    }

    const parentRef = input.parent as string | undefined;
    const positionRead = readVec3(input, 'position');
    if (positionRead.error) return positionRead.error;
    const position = positionRead.value;
    const name = (input.name as string | undefined) ?? artifact.name;
    // A style supplies the whole theme (materials, tokens, fonts); an explicit
    // palette overrides the colour roles it names. Neither is baked — both are
    // declared on the placed root, and the converted nodes bind to them.
    const requestedStyle = (input.style as string | undefined)?.trim();
    const styleRef = pins.style ?? requestedStyle;
    const style = styleRef ? getStyle(styleRef) : null;
    const palette = input.palette as TemplatePalette | undefined;
    const { traceScene } = ctx;

    let parentObj: SceneObject | null = null;
    if (parentRef) {
        parentObj = resolveObject(parentRef, ctx);
        if (!parentObj) return `Error: Parent object "${parentRef}" not found.`;
    }

    // Embedded components use the measured shrink limits.
    const conv = convertTemplate(artifact, { componentMetrics: COMPONENT_METRICS });
    conv.nodes[0].data.name = name;

    const created: SceneObject[] = [];
    /** Actual path of each created node relative to the root ("" = root) — names may have been deduped. */
    const actualPaths: string[] = [];
    for (const node of conv.nodes) {
        const parent = node.parentIndex < 0
            ? (parentObj ?? topLevelParent(traceScene) ?? undefined)
            : created[node.parentIndex];
        const obj = await traceScene.createSceneObjectFromData(node.data, parent);
        created.push(obj);
        actualPaths.push(node.parentIndex < 0
            ? ''
            : [actualPaths[node.parentIndex], obj.name].filter(Boolean).join('/'));
    }

    const root = created[0];
    if (position) applyTransform(root.transform, position, undefined, undefined);
    // The root is the theme provider: everything the conversion bound resolves
    // through it, and restyling later is one commit here rather than a repaint
    // pass over the subtree.
    await declareTheme(root, style, palette);

    const childEntries: CreateChildEntry[] = conv.nodes.slice(1).map((node, i) => ({
        object: created[i + 1],
        data: created[i + 1].getSceneObjectData(),
        parent: created[node.parentIndex],
    }));
    recordCreateForUndo(root, parentObj ?? topLevelParent(traceScene), childEntries);
    traceScene.select(root);
    ctx.onSceneChanged?.();

    const pal = { ...DEFAULT_PALETTE, ...palette };
    const themeNote = style
        ? `themed "${style.label}"`
        : `background ${pal.background}, surface ${pal.surface}`;
    return `Placed template "${slug}" as "${root.name}" (${created.length} objects, ${themeNote}). Surfaces and text BIND to the theme declared on the root, so restyling later is a single change there — do not paint them with literal colors. Slots flow their children via auto-layout.${pinNote([['style', pins.style, requestedStyle]])}

${conv.guidance.length ? `${conv.guidance.join(' ')}\n\n` : ''}SLOTS to fill:
${formatSlots(root.name, conv.slots, actualPaths)}

${PLACE_TEMPLATE_GUIDANCE.replace('{accent}', pal.accent).replace('{text}', pal.text)}`;
}

// ── stage_display ──────────────────────────────────────────

function describeWrapper(w: { id: string; wrapperKind: string | null; description: string | null; sizes: Record<string, unknown> }): string {
    return `- ${w.id} [${w.wrapperKind ?? '?'}] (${Object.keys(w.sizes).join('/')}) — ${w.description ?? ''}`;
}

/**
 * Themed Renderer data for a design-set GLB: per mesh slot, a BINDING to the
 * material role that slot plays.
 *
 * The design sets author the seven material roles as their material ids, so the
 * mapping is name → role → `$<role><Suffix>`. This is adoption done at
 * conversion time — it needs no live scene and no mesh inspection, so it still
 * works headless, but unlike baking the style's values it leaves the scene
 * *referencing* the theme. Swapping the style afterwards is one commit on the
 * provider; every bound slot re-resolves, with nothing to re-walk or repaint.
 *
 * Only roles the design set actually uses are bound, and every suffix is bound
 * whether or not the current style sets it: an unresolved binding does not
 * drive (the GLB's own value shows), and it starts driving the moment a style
 * supplies that role — which is what makes a partial style safe.
 */
export function themedRenderer(src: string, _style: LibraryStyle): RendererData | null {
    const file = src.split('/').pop() ?? '';
    const names = COMPONENT_MATERIALS[file];
    if (!names?.length) return null;
    const materials: NonNullable<RendererData['materials']> = [];
    names.forEach((name, index) => {
        const role = name ? resolveMaterialRole(name) : null;
        if (!role) return;
        materials.push({
            targetMaterialIndex: index,
            src: `$${rolePropertyName(role, 'src')}`,
            baseColorFactor: `$${rolePropertyName(role, 'color')}`,
            // Numeric fields carrying a binding STRING — the `opacity`
            // precedent; the Renderer's `typeof === 'number'` presence gates
            // read an unresolved one as absent.
            roughnessFactor: `$${rolePropertyName(role, 'roughness')}` as unknown as number,
            metallicFactor: `$${rolePropertyName(role, 'metallic')}` as unknown as number,
        });
    });
    return materials.length ? { '@type': 'Renderer', materials } : null;
}

/** Declare the local style values and return their applied undo command.
 * Placement composes this command; a standalone style change uses record: true.
 */
export async function declareTheme(
    root: SceneObject,
    style: LibraryStyle | null,
    palette?: TemplatePalette,
    options: { record?: boolean } = {},
): Promise<UndoCommand | null> {
    if (style) await initHostFontPacks();
    if (root.transform.isDisposed()) throw new Error('The object was removed before its style could be applied.');
    const values: Record<string, string | number | boolean> = { ...(style?.values ?? {}) };
    // An explicit palette from the model overrides the style's tokens for the
    // roles it names — the same precedence the tool documented when these were
    // baked, expressed as theme values instead.
    const pal = { ...DEFAULT_PALETTE, ...palette };
    if (palette || !style) {
        values.backgroundColor = pal.background;
        values.panelColor = pal.surface;
        values.accentColor = pal.accent;
        values.backgroundTextColor = pal.text;
    }
    // Resolve against the completed host catalogue before persisting family
    // names in the theme. Undeclared families map to bundled faces; Text keeps
    // its property binding, and a later style application resolves anew.
    for (const role of ['title', 'header', 'body', 'mono'] as const) {
        const key = `${role}Font`;
        const authored = values[key];
        if (typeof authored !== 'string') continue;
        const bundled = mapFontToBundled(authored);
        if (bundled) values[key] = bundled;
    }

    if (Object.keys(values).length === 0) return null;
    const result = await setThemeValues(root, values, { record: options.record ?? false });
    return result.command;
}

/** Viewer failure codes keep the existing tool and panel result text. */
export function wrapperOperationMessage(
    error: WrapperOperationError,
    context: { targetName?: string; wrapperId: string; sizes: string[] },
): string {
    switch (error.code) {
        case 'NOT_A_WRAPPER':
            return 'No wrapper is staged around this object — pick a wrapper first, then a form.';
        case 'NO_PARENT':
            return context.targetName
                ? `Error: "${context.targetName}" is the root of this document, which cannot be moved into a wrapper. Select an object inside it.`
                : 'Error: no document root to return the content to.';
        case 'NOT_A_WRAPPER_ASSET':
            return `Error: no wrapper "${context.wrapperId}".`;
        case 'NO_SIBLING':
            return `Error: wrapper "${context.wrapperId}" has sizes: ${context.sizes.join(', ')}.`;
        case 'SAME_ASSET':
            return 'Wrapper already uses this asset.';
        case 'NO_SLOT':
        case 'AMBIGUOUS_SLOT':
        case 'REFUSED':
        case 'CHANGED':
        case 'NOT_RESTORED':
            return `Error: could not mount "${context.targetName ?? context.wrapperId}" into the wrapper: ${error.message}`;
    }
}

const MAX_WRAPPER_SUGGESTIONS = 8;

/** Record the operation, any reference copy and an optional theme as one action. */
export async function runWrapperAction(
    ctx: ToolContext,
    selected: SceneObject | null,
    operation: () => Promise<WrapperOperation>,
    success: (result: WrapperOperation) => string,
    wrapperId: string,
    finish?: (result: WrapperOperation, commands: UndoCommand[]) => Promise<void>,
): Promise<{ ok: boolean; message: string }> {
    const commands: UndoCommand[] = [];
    let completedOperation: WrapperOperation | null = null;
    try {
        const folder = getActiveCmsManager()?.folderUploadTarget();
        if (selected) {
            const copy = await copyReferencesForInsideEdit(selected, { folder });
            if ('refused' in copy) return { ok: false, message: `"${selected.name}" is inside a library asset that could not be copied into this scene, so it was not changed.` };
            if (copy.command) commands.push(copy.command);
            if (copy.orphaned.length) console.warn('Wrapper reference copy could not copy files:', copy.orphaned);
        }
        const result = await operation();
        completedOperation = result;
        commands.push(result.command);
        ctx.traceScene.select(result.wrapper ?? result.content[0] ?? null);
        await finish?.(result, commands);
        return { ok: true, message: success(result) };
    } catch (error) {
        if (completedOperation) {
            return { ok: true, message: `${success(completedOperation)} Note: the wrapper change completed, but follow-up edits failed: ${error instanceof Error ? error.message : String(error)}` };
        }
        if (!(error instanceof WrapperOperationError && error.command)) {
            try {
                while (commands.length) {
                    await commands.at(-1)!.undo();
                    commands.pop();
                }
            } catch (rollbackError) {
                return { ok: false, message: `Error: could not restore the copied reference: ${String(rollbackError)}` };
            }
        }
        if (error instanceof WrapperOperationError) {
            if (error.command) commands.push(error.command);
            return {
                ok: false,
                message: wrapperOperationMessage(error, {
                    targetName: selected?.name, wrapperId,
                    sizes: Object.keys(getWrapper(wrapperId)?.sizes ?? {}),
                }),
            };
        }
        return { ok: false, message: `Error: ${error instanceof Error ? error.message : String(error)}` };
    } finally {
        if (commands.length) {
            const undo = getUndoManager();
            if (!undo.isUndoRedoing) {
                undo.push(commands.length === 1 ? commands[0] : new CompositeCommand('Change wrapper', commands));
            }
            ctx.onSceneChanged?.();
        }
    }
}

/** Shared panel/tool staging: resolve the asset, copy references, then record one action. */
export async function stageWrapperAction(
    ctx: ToolContext,
    target: SceneObject | null,
    wrapperId: string,
    opts: { style?: string; set?: string; size?: string; name?: string; position?: [number, number, number]; detailed?: boolean } = {},
): Promise<{ ok: boolean; message: string }> {
    if (target && isStructuralRoot(target)) {
        return { ok: false, message: `Error: "${target.name}" is the root of this document, which cannot be moved into a wrapper. Select an object inside it.` };
    }
    const wrapper = getWrapper(wrapperId);
    const family = wrapper?.id ?? wrapperId;
    const label = wrapper?.label ?? wrapperId;
    const style = opts.style ? getStyle(opts.style) : null;
    if (opts.style && !style) return { ok: false, message: `No style "${opts.style}".` };
    const library = await getWrapperLibrary();
    const existing = target ? findWrapper(target) : null;
    const from = existing?.getBehavior(Provenance)?.getData().placedFrom;
    const previous = from ? await library.describe(from) : null;
    const width = target?.getBehavior(Layout)?.getData().width;
    const byWidth = typeof width === 'number' && width > 0
        ? Object.entries(wrapper?.sizes ?? {}).filter(([, value]) => value.content.width)
            .sort(([, a], [, b]) => Math.abs(a.content.width! * DU_TO_M - width) - Math.abs(b.content.width! * DU_TO_M - width))
        : [];
    const previousSize = previous && (previous.family === family || wrapper?.sizes[previous.size])
        ? previous.size : undefined;
    const size = opts.size ?? previousSize ?? byWidth[0]?.[0] ?? (wrapper ? defaultSize(wrapper) : 'md');
    const carriedSetAssetUrl = previous && previous.family !== family
        ? await library.find({ family, size, set: previous.set }) : null;
    const previousSet = previous && (previous.family === family || carriedSetAssetUrl)
        ? previous.set : undefined;
    const set = opts.set ?? previousSet ?? style?.meta.recommendedSets[0] ?? 'Core';
    const assetUrl = set === previousSet && carriedSetAssetUrl
        ? carriedSetAssetUrl : await library.find({ family, size, set });
    if (!assetUrl) {
        if (wrapper) return { ok: false, message: wrapperOperationMessage(new WrapperOperationError('NO_SIBLING', ''), {
            targetName: target?.name, wrapperId: family, sizes: Object.keys(wrapper.sizes),
        }) };
        const near = searchWrappers(wrapperId).slice(0, MAX_WRAPPER_SUGGESTIONS);
        return { ok: false, message: `Error: no wrapper "${wrapperId}".${near.length ? ` Closest:\n${near.map(describeWrapper).join('\n')}` : ''}` };
    }
    const { traceScene } = ctx;
    const parent = topLevelParent(traceScene);
    if (!target && !parent) return { ok: false, message: 'Error: no document root to return the content to.' };
    const name = opts.name ?? `${label} Stage`;
    let styleApplied = false;
    return runWrapperAction(ctx, existing ?? target, () => {
        if (!existing) return target ? wrapContent(target, assetUrl, { name })
            : placeWrapper(parent!, assetUrl, { name, position: opts.position });
        if (previous?.family === family) {
            if (previous.set === set && previous.size !== size) return resizeWrapper(existing, size, library);
            if (previous.size === size && previous.set !== set) return changeWrapperSet(existing, set, library);
        }
        return swapWrapper(existing, assetUrl);
    }, operation => {
        const root = operation.wrapper!;
        const summary = `Staged "${family}" (${size}, set ${set}) as "${root.name}"`;
        const styleNote = style && !styleApplied ? ` Style "${style.meta.name}" was not applied.` : '';
        if (!opts.detailed) return `${summary}.${styleNote}`;
        const mount = findWrapperSlot(root)!;
        const contentObjects = new Set(operation.content.flatMap(content => [content, ...content.getDescendants()]));
        const createdCount = [root, ...root.getDescendants()].filter(object => !contentObjects.has(object)).length;
        const mountedLine = target ? `\n"${target.name}" is mounted in the stage.` : '';
        const mountName = mount === root ? root.name : `${root.name}/…/${mount.name}`;
        const styledLine = styleApplied
            ? ` Set pieces are themed with the "${style!.meta.name}" style.`
            : styleNote || ' Pass a style to theme the set pieces (strongly recommended).';
        return `${summary} (${createdCount} objects; design-set geometry included).${mountedLine}
Content mount: "${mountName}"${target ? '' : ' — parent the finished piece into it (its layout centers and fits content).'}${styledLine}`;
    }, family, async (operation, commands) => {
        const root = operation.wrapper!;
        if (existing && opts.name !== undefined && root.name !== opts.name) {
            const oldName = root.name;
            traceScene.renameSceneObject(root, opts.name);
            commands.push(new RenameCommand(root, oldName, root.name));
        }
        if (target && opts.position) {
            const before = TransformCommand.capture(root);
            applyTransform(root.transform, opts.position, undefined, undefined);
            root.captureTransform();
            root.refreshSceneData();
            commands.push(new TransformCommand(root, before, TransformCommand.capture(root)));
        }
        if (!target || style) {
            const theme = await declareTheme(root, style);
            if (theme) commands.push(theme);
            styleApplied = !!style;
        }
    });
}

async function executeStageDisplay(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const pins = ctx.assistantPins ?? {};
    const requested = (key: string) => (input[key] as string | undefined)?.trim();
    const wrapperId = pins.wrapper ?? requested('wrapper');
    if (!wrapperId) {
        return `Error: "wrapper" is required. Staging catalog:\n${WRAPPER_LIBRARY.map(describeWrapper).join('\n')}`;
    }
    const targetRef = input.target as string | undefined;
    const target = targetRef ? resolveObject(targetRef, ctx) : null;
    if (targetRef && !target) return `Error: target object "${targetRef}" not found.`;
    const positionRead = readVec3(input, 'position');
    if (positionRead.error) return positionRead.error;
    const styleRef = pins.style ?? requested('style');
    if (styleRef && !getStyle(styleRef)) return `Error: no style "${styleRef}". Styles are listed by search_styles-like slugs, e.g. modern-gothic, beachside, gallery.`;
    const wrapper = getWrapper(wrapperId);
    let size = requested('size') || undefined;
    if (pins.wrapper && size && wrapper && !wrapper.sizes[size]) size = undefined;
    const result = await stageWrapperAction(ctx, target, wrapperId, {
        style: styleRef, set: pins.set ?? (requested('set') || undefined), size,
        name: input.name as string | undefined, position: positionRead.value, detailed: true,
    });
    return result.message + (result.ok ? pinNote([
        ['wrapper', pins.wrapper, requested('wrapper')],
        ['style', pins.style, requested('style')],
        ['set', pins.set, requested('set')],
    ]) : '');
}

// ── definitions ────────────────────────────────────────────

export const templateTools: ToolDefinition[] = [
    {
        tool: {
            name: 'search_templates',
            input_schema: {
                type: 'object' as const,
                properties: {
                    query: { type: 'string' },
                },
            },
        },
        execute: executeSearchTemplates,
    },
    {
        tool: {
            name: 'place_template',
            input_schema: {
                type: 'object' as const,
                properties: {
                    slug: { type: 'string' },
                    name: { type: 'string' },
                    parent: { type: 'string' },
                    style: { type: 'string' },
                    palette: {
                        type: 'object',
                        properties: {
                            background: { type: 'string' },
                            surface: { type: 'string' },
                            accent: { type: 'string' },
                            text: { type: 'string' },
                        },
                    },
                    position: {
                        type: 'array', items: { type: 'number' }, minItems: 3, maxItems: 3,
                    },
                },
                required: ['slug'],
            },
        },
        execute: editModeOnly(executePlaceTemplate),
    },
    {
        tool: {
            name: 'stage_display',
            input_schema: {
                type: 'object' as const,
                properties: {
                    wrapper: { type: 'string' },
                    target: { type: 'string' },
                    style: { type: 'string' },
                    set: { type: 'string' },
                    size: { type: 'string' },
                    name: { type: 'string' },
                    position: {
                        type: 'array', items: { type: 'number' }, minItems: 3, maxItems: 3,
                    },
                },
            },
        },
        execute: editModeOnly(executeStageDisplay),
    },
];
